/**
 * Classifiable File Extensions
 *
 * Canonical list of file extensions supported by the document/email
 * classification steps. Shared between backend (validation/filtering)
 * and frontend (config UI multi-select).
 *
 * Extensions are stored lowercase without a leading dot.
 */

export interface ClassifiableFileExtension {
  /** Lowercase extension without leading dot (e.g. "xlsx") */
  extension: string;
  /** Human-readable label for UI display */
  label: string;
  /** Grouping for multi-select UI */
  group: "Spreadsheet" | "Document" | "Image";
  /**
   * MIME types that identify this format when the file name carries no
   * usable extension (e.g. a remittance export named `SupRem-00228727`).
   */
  mimeTypes: readonly string[];
}

export const CLASSIFIABLE_FILE_EXTENSIONS: readonly ClassifiableFileExtension[] =
  [
    // Spreadsheet
    {
      extension: "xlsx",
      label: "Excel (.xlsx)",
      group: "Spreadsheet",
      mimeTypes: [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ],
    },
    {
      extension: "xls",
      label: "Excel 97-2003 (.xls)",
      group: "Spreadsheet",
      mimeTypes: ["application/vnd.ms-excel"],
    },
    {
      extension: "csv",
      label: "CSV (.csv)",
      group: "Spreadsheet",
      mimeTypes: ["text/csv"],
    },

    // Document
    {
      extension: "pdf",
      label: "PDF (.pdf)",
      group: "Document",
      mimeTypes: ["application/pdf"],
    },
    {
      extension: "txt",
      label: "Text (.txt)",
      group: "Document",
      mimeTypes: ["text/plain"],
    },

    // Image
    {
      extension: "png",
      label: "PNG (.png)",
      group: "Image",
      mimeTypes: ["image/png"],
    },
    {
      extension: "jpg",
      label: "JPEG (.jpg)",
      group: "Image",
      mimeTypes: ["image/jpeg"],
    },
    {
      extension: "jpeg",
      label: "JPEG (.jpeg)",
      group: "Image",
      mimeTypes: ["image/jpeg"],
    },
  ] as const;

/** Just the extension strings, for Zod schema validation */
export const CLASSIFIABLE_EXTENSIONS: readonly string[] =
  CLASSIFIABLE_FILE_EXTENSIONS.map((f) => f.extension);
